/**
 * Project Board Layout Settings Modal
 * Handles column visibility preferences for the project kanban board
 */

import { getCookie } from '../common/util/util.js';

document.addEventListener('DOMContentLoaded', function() {
    const layoutSettingsBtn = document.getElementById('layoutSettingsBtn');
    const layoutModal = document.getElementById('boardLayoutModal');

    if (!layoutSettingsBtn || !layoutModal) return;

    const closeModalBtn = layoutModal.querySelector('.close-modal');
    const cancelBtn = layoutModal.querySelector('.cancel-btn');
    const saveBtn = layoutModal.querySelector('.save-layout-btn');

    layoutSettingsBtn.addEventListener('click', () => {
        layoutModal.classList.add('visible');
    });

    function closeModal() {
        layoutModal.classList.remove('visible');
    }

    closeModalBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);
    layoutModal.addEventListener('click', (e) => {
        if (e.target === layoutModal) closeModal();
    });

    // === Drag-to-reorder items in the modal ===
    const checkboxContainer = layoutModal.querySelector('.columns-checkboxes');
    let draggedItem = null;

    checkboxContainer.addEventListener('dragstart', (e) => {
        const item = e.target.closest('.form-check');
        if (!item) return;
        draggedItem = item;
        item.classList.add('modal-dragging');
        e.dataTransfer.effectAllowed = 'move';
    });

    checkboxContainer.addEventListener('dragend', () => {
        if (draggedItem) {
            draggedItem.classList.remove('modal-dragging');
            draggedItem = null;
        }
        checkboxContainer.querySelectorAll('.form-check').forEach(el => {
            el.classList.remove('drag-insert-above', 'drag-insert-below');
        });
    });

    checkboxContainer.addEventListener('dragover', (e) => {
        if (!draggedItem) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        const target = e.target.closest('.form-check');
        if (!target || target === draggedItem) return;

        // Clear all indicators
        checkboxContainer.querySelectorAll('.form-check').forEach(el => {
            el.classList.remove('drag-insert-above', 'drag-insert-below');
        });

        // Show indicator above or below based on mouse position
        const rect = target.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (e.clientY < midY) {
            target.classList.add('drag-insert-above');
        } else {
            target.classList.add('drag-insert-below');
        }
    });

    checkboxContainer.addEventListener('drop', (e) => {
        e.preventDefault();
        const target = e.target.closest('.form-check');
        if (!target || !draggedItem || target === draggedItem) return;

        const rect = target.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (e.clientY < midY) {
            checkboxContainer.insertBefore(draggedItem, target);
        } else {
            checkboxContainer.insertBefore(draggedItem, target.nextSibling);
        }

        checkboxContainer.querySelectorAll('.form-check').forEach(el => {
            el.classList.remove('drag-insert-above', 'drag-insert-below');
        });
    });

    // === Save: send both hidden_columns and column_order ===
    saveBtn?.addEventListener('click', function() {
        const allItems = checkboxContainer.querySelectorAll('.form-check');
        const parseValue = v => v === 'none' ? 'none' : parseInt(v);

        const checkedIds = new Set(
            Array.from(layoutModal.querySelectorAll('.column-checkbox:checked'))
                .map(cb => parseValue(cb.value))
        );

        // hidden_columns = all statuses that are NOT checked
        const hiddenColumns = Array.from(layoutModal.querySelectorAll('.column-checkbox'))
            .filter(cb => !cb.checked)
            .map(cb => parseValue(cb.value));

        // column_order = DOM order of all items (for visible column ordering)
        const columnOrder = Array.from(allItems)
            .map(item => parseValue(item.dataset.columnId));

        if (checkedIds.size === 0) {
            alert('Please select at least one column to display');
            return;
        }

        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving...';

        fetch('/projects/api/board-layout/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify({
                hidden_columns: hiddenColumns,
                column_order: columnOrder,
            }),
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                closeModal();
                window.location.reload();
            } else {
                alert(data.error || 'Failed to save settings');
                saveBtn.disabled = false;
                saveBtn.textContent = 'Save';
            }
        })
        .catch(error => {
            console.error('Error saving board layout:', error);
            alert('Failed to save settings');
            saveBtn.disabled = false;
            saveBtn.textContent = 'Save';
        });
    });

    // Organisation filter with localStorage persistence
    const orgFilter = document.getElementById('orgFilter');
    if (orgFilter) {
        const STORAGE_KEY = 'projectBoard_selectedOrg';

        // Restore from localStorage if no URL param is set
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.has('org')) {
            const savedOrg = localStorage.getItem(STORAGE_KEY);
            if (savedOrg) {
                // Check that the saved org exists in the dropdown options
                const optionExists = Array.from(orgFilter.options).some(o => o.value === savedOrg);
                if (optionExists) {
                    const url = new URL(window.location.href);
                    url.searchParams.set('org', savedOrg);
                    window.location.replace(url.toString());
                    return; // Stop execution, page will reload
                } else {
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        }

        orgFilter.addEventListener('change', function() {
            const url = new URL(window.location.href);
            if (this.value) {
                url.searchParams.set('org', this.value);
                localStorage.setItem(STORAGE_KEY, this.value);
            } else {
                url.searchParams.delete('org');
                localStorage.removeItem(STORAGE_KEY);
            }
            window.location.href = url.toString();
        });
    }
});
